import {
  Directive,
  ElementRef,
  AfterViewInit,
  Input,
  ViewContainerRef,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  Inject
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { JoyrideStepsContainerService } from '../services/joyride-steps-container.service';
import { JoyrideError, JoyrideStep } from '../joyride.domain';
import { JoyrideConstants } from '../joyride.constants';

@Directive({
  selector: 'tdWbJoyrideStep, [tdWbJoyrideStep]',
  standalone: false
})
export class JoyrideDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('tdWbJoyrideStep') name: string;
  @Input() nextStep?: string;
  @Input() title?: string | Observable<string>;
  @Input() text?: string | Observable<string>;
  @Input() customLabel?: string | Observable<string>;
  @Input() iconSrc?: string;
  @Input() stepPosition?: string = JoyrideConstants.NO_POSITION;
  @Input() usedAsCoachMark?: boolean = false;
  @Output() back?: EventEmitter<void> = new EventEmitter();
  @Output() next?: EventEmitter<void> = new EventEmitter();
  @Output() done?: EventEmitter<void> = new EventEmitter();

  private step: JoyrideStep;
  private readonly destroy: Subject<void> = new Subject<void>();

  constructor(
    @Inject('window') private browserWindow: Window,
    private readonly joyrideStepsContainer: JoyrideStepsContainerService,
    private viewContainerRef: ViewContainerRef
  ) {
    this.step = new JoyrideStep();
  }

  ngAfterViewInit(): void {
    this.step.position = this.stepPosition;
    this.step.targetViewContainer = this.viewContainerRef;
    this.setAsyncFields(this.step);
    this.step.nextClicked = this.next;
    this.step.backCliked = this.back;
    this.step.tourDone = this.done;
    if (!this.name) {
      throw new JoyrideError('All the steps should have the "joyrideStep" property set with a custom name.');
    }
    this.step.name = this.name;
    this.step.iconSrc = this.iconSrc;
    this.step.transformCssStyle = this.browserWindow.getComputedStyle(this.viewContainerRef.element.nativeElement).transform;
    this.step.isElementOrAncestorFixed =
      this.isElementFixed(this.viewContainerRef.element) ||
      this.isAncestorsFixed(this.viewContainerRef.element.nativeElement.parentElement);
    this.step.usedAsCoachMark = this.usedAsCoachMark;

    this.joyrideStepsContainer.addStep(this.step);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['title'] || changes['text'] || changes['customLabel']) {
      this.setAsyncFields(this.step);
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  private isElementFixed(element: ElementRef): boolean {
    return this.browserWindow.getComputedStyle(element.nativeElement).position === 'fixed';
  }

  private setAsyncFields(step: JoyrideStep): void {
    if (this.title instanceof Observable) {
      this.title
        .pipe(takeUntil(this.destroy))
        .subscribe((title: string) => {
          step.title.next(title);
        });
    } else {
      step.title.next(this.title);
    }
    if (this.text instanceof Observable) {
      this.text
        .pipe(takeUntil(this.destroy))
        .subscribe((text: string) => {
          step.text.next(text);
        });
    } else {
      step.text.next(this.text);
    }
    if (this.customLabel instanceof Observable) {
      this.customLabel
        .pipe(takeUntil(this.destroy))
        .subscribe((label: string) => {
          step.customLabel.next(label);
        });
    } else {
      step.customLabel.next(this.customLabel);
    }
  }

  private isAncestorsFixed(nativeElement: HTMLElement): boolean {
    if (!nativeElement || !nativeElement.parentElement) {
      return false;
    }
    const isElementFixed = this.browserWindow.getComputedStyle(nativeElement.parentElement).position === 'fixed';
    if (nativeElement.nodeName === 'BODY') {
      return isElementFixed;
    }
    if (isElementFixed) {
      return true;
    } else {
      return this.isAncestorsFixed(nativeElement.parentElement);
    }
  }
}
